import React, { createContext, useContext, useState } from "react";
import { BULK_EDIT_PAGE_SIZE } from "../components/bulkEdit/BulkEditConstants";

export interface BulkEditRow {
  projectId: string;
  identifierFields: Record<string, string>;
  editableFields: Record<string, EditableFieldData>;
  // Editable versions of fixed identifier columns (Status, LMSC/SC-SC, Timeline).
  // Kept separate from editableFields so they never appear in the Column Filter
  // and always render in their fixed identifier position/styling.
  identifierEditableFields: Record<string, EditableFieldData>;
}

export interface EditableFieldData {
  value: string;
  originalValue: string;
  fieldType: string;
  required: boolean;
  options?: string[];
  fieldGroup: "buildingAttributes" | "productivity" | "siteDetails" | "retrofitDetails";
}

export type CellState = "pristine" | "valid-modified" | "invalid" | "saving" | "saved" | "formula-updated";

interface BulkEditStateProps {
  rows: BulkEditRow[];
  setRows(rows: BulkEditRow[]): void;
  originalRows: BulkEditRow[];
  setOriginalRows(rows: BulkEditRow[]): void;
  visibleColumns: string[];
  setVisibleColumns(columns: string[]): void;
  cellStates: Record<string, CellState>;
  setCellStates(cellStates: Record<string, CellState>): void;
  currentPage: number;
  setCurrentPage(page: number): void;
  pageSize: number;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges(flag: boolean): void;
  isLoading: boolean;
  setIsLoading(flag: boolean): void;
  isSaving: boolean;
  setIsSaving(flag: boolean): void;
}

const initialState: BulkEditStateProps = {
  rows: [],
  setRows: () => {},
  originalRows: [],
  setOriginalRows: () => {},
  visibleColumns: [],
  setVisibleColumns: () => {},
  cellStates: {},
  setCellStates: () => {},
  currentPage: 1,
  setCurrentPage: () => {},
  pageSize: BULK_EDIT_PAGE_SIZE,
  hasUnsavedChanges: false,
  setHasUnsavedChanges: () => {},
  isLoading: true,
  setIsLoading: () => {},
  isSaving: false,
  setIsSaving: () => {},
};

const BulkEditContext = createContext(initialState);

export function BulkEditContextProvider({ children }) {
  const [userRows, setUserRows] = useState<BulkEditRow[]>([]);
  const [userOriginalRows, setUserOriginalRows] = useState<BulkEditRow[]>([]);
  const [userVisibleColumns, setUserVisibleColumns] = useState<string[]>([]);
  const [userCellStates, setUserCellStates] = useState<Record<string, CellState>>({});
  const [userCurrentPage, setUserCurrentPage] = useState(1);
  const [userHasUnsavedChanges, setUserHasUnsavedChanges] = useState(false);
  const [userIsLoading, setUserIsLoading] = useState(true);
  const [userIsSaving, setUserIsSaving] = useState(false);

  const context: BulkEditStateProps = {
    rows: userRows,
    setRows: setUserRows,
    originalRows: userOriginalRows,
    setOriginalRows: setUserOriginalRows,
    visibleColumns: userVisibleColumns,
    setVisibleColumns: setUserVisibleColumns,
    cellStates: userCellStates,
    setCellStates: setUserCellStates,
    currentPage: userCurrentPage,
    setCurrentPage: setUserCurrentPage,
    pageSize: BULK_EDIT_PAGE_SIZE,
    hasUnsavedChanges: userHasUnsavedChanges,
    setHasUnsavedChanges: setUserHasUnsavedChanges,
    isLoading: userIsLoading,
    setIsLoading: setUserIsLoading,
    isSaving: userIsSaving,
    setIsSaving: setUserIsSaving,
  };

  return (
    <BulkEditContext.Provider value={context}>{children}</BulkEditContext.Provider>
  );
}

export const useBulkEditContext = () => useContext(BulkEditContext);

export default BulkEditContext;
